import { useState, useEffect } from 'react';
import * as specialtyService from '../services/specialtyService';
import { useUIStore } from '../stores/uiStore';
import { ISpecialty } from '../types/interfaces/ISpecialty';
import { notifyApiError } from '../services/core/apiErrorUX';
import { notifyApiSuccess } from '../services/core/apiSuccessUX';

// Hook específico para especialidades
export function useSpecialties() {
  const [loading, setLoading] = useState(false);
  const [specialties, setSpecialties] = useState<ISpecialty[]>([]);
  const { addNotification } = useUIStore();

  // Cargar especialidades al inicializar el hook
  useEffect(() => {
    loadSpecialties();
  }, []);

  const loadSpecialties = async () => {
    setLoading(true);
    try {
      const response = await specialtyService.getSpecialties();

      if (response.success && response.data) {
        setSpecialties(response.data);
        return response.data; // Retornar data directa
      } else {
        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al cargar especialidades',
        });
        return null;
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Error al cargar especialidades',
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getAllSpecialties = async () => {
    setLoading(true);
    try {
      const response = await specialtyService.getSpecialties();

      if (response.success && response.data) {
        setSpecialties(response.data);
        return response.data; // Retornar data directa
      } else {
        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al cargar especialidades',
        });
        return null;
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Error al cargar especialidades',
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getSpecialtyById = async (id: string) => {
    setLoading(true);
    try {
      const response = await specialtyService.getSpecialtyById(id);

      if (response.success && response.data) {
        return response.data; // Retornar data directa
      } else {
        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al cargar especialidad',
        });
        return null;
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Error al cargar especialidad',
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const createSpecialty = async (specialtyData: any) => {
    setLoading(true);
    try {
      const response = await specialtyService.createSpecialty(specialtyData);

      if (response.success && response.data) {
        setSpecialties(prev => [...prev, response.data]);
        notifyApiSuccess(addNotification, 'Especialidad Creada', 'Especialidad creada correctamente');
        return response.data; // Retornar data directa
      } else {
        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al crear especialidad',
        });
        return null;
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Error al crear especialidad',
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateSpecialty = async (id: string, specialtyData: any) => {
    setLoading(true);
    try {
      const response = await specialtyService.updateSpecialty(id, specialtyData);

      if (response.success && response.data) {
        setSpecialties(prev =>
          prev.map(s => (s.id === id ? response.data! : s))
        );
        notifyApiSuccess(addNotification, 'Especialidad Actualizada', 'Especialidad actualizada correctamente');
        return response.data; // Retornar data directa
      } else {
        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al actualizar especialidad',
        });
        return null;
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Error al actualizar especialidad',
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteSpecialty = async (id: string) => {
    setLoading(true);
    try {
      const response = await specialtyService.deleteSpecialty(id);

      if (response.success) {
        setSpecialties(prev => prev.filter(s => s.id !== id));
        notifyApiSuccess(addNotification, 'Especialidad Eliminada', 'Especialidad eliminada correctamente');
        return true; // Retornar éxito
      } else {
        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al eliminar especialidad',
        });
        return false;
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Error al eliminar especialidad',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const toggleSpecialtyStatus = async (id: string) => {
    setLoading(true);
    try {
      const response = await specialtyService.toggleSpecialtyStatus(id);

      if (response.success && response.data) {
        setSpecialties(prev =>
          prev.map(s => (s.id === id ? response.data! : s))
        );
        notifyApiSuccess(addNotification, 'Estado Cambiado', 'Estado de especialidad cambiado correctamente');
        return response.data; // Retornar data directa
      } else {
        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al cambiar estado',
        });
        return null;
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Error al cambiar estado',
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    specialties, // Datos directos del estado local
    loading,
    getAllSpecialties, // Retorna ISpecialty[] o null
    getSpecialtyById, // Retorna ISpecialty o null
    createSpecialty, // Retorna ISpecialty o null
    updateSpecialty, // Retorna ISpecialty o null
    deleteSpecialty, // Retorna boolean
    toggleSpecialtyStatus, // Retorna ISpecialty o null
    refreshSpecialties: loadSpecialties, // Para recargar si es necesario
  };
}
